// Owner components shared by the company page, the Owners portfolio and the investor's record (Task K, prototype §33).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Send, Stamp } from 'lucide-react';
import { Badge, Button, Field, Input, Modal, Money, Select, Textarea, SkeletonRows } from '../ui';
import { FormError, FormGrid, Wide, toMajor, toMinor } from '../forms/common';
import { useCan } from '../../hooks/usePermission';
import { formatDate } from '../../lib/viz';
import {
  ownersApi, statementBlobUrl, downloadStatement, periodLabel, PERIODS, quarterOf, todayIso, INVESTOR_KIND_LABEL,
  type CompanySummary, type Holding, type InvestorKind, type OwnerStatement, type Ownership, type PeriodKey,
} from '../../lib/owners';

export function PeriodPicker({ value, onChange }: { value: PeriodKey; onChange: (k: PeriodKey) => void }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value as PeriodKey)} aria-label="Period">
      {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
    </Select>
  );
}

/** The company's money in three floors: the buildings, the company after the fee, (the investors are a table elsewhere). */
export function ThreeFloors({ s }: { s: CompanySummary }) {
  const row = (label: string, minor: number, tone: 'in' | 'out' | 'sub' | 'total' | 'net' = 'in', extra?: React.ReactNode) => (
    <div className={`flex items-baseline justify-between gap-3 py-1.5 ${tone === 'sub' ? 'pl-4 text-[12.5px] text-ink-500' : 'text-[13px]'} ${tone === 'total' ? 'border-t border-ink-200 font-medium' : ''} ${tone === 'net' ? 'mt-1 rounded-lg bg-ink-50 px-3 py-2 text-[13px] font-semibold' : ''}`}>
      <span className="min-w-0">{label}{extra}</span>
      <Money minor={tone === 'out' ? -minor : minor} currency={s.currency} className={tone === 'out' ? 'shrink-0 text-red-700' : tone === 'net' ? (minor < 0 ? 'shrink-0 text-red-700' : 'shrink-0 text-emerald-700') : 'shrink-0 text-ink-900'} />
    </div>
  );
  return (
    <div className="space-y-2.5">
      <section>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500"><span className="text-bronze-600">1</span> · The buildings</p>
        {row('Rent earned', s.rentMinor)}
        {s.otherIncome.map((o) => <div key={o.code}>{row(o.name, o.amountMinor)}</div>)}
        {s.operatingCosts.groups.map((g) => (
          <div key={g.key}>
            {row(g.label, g.amountMinor, 'out')}
            {g.accounts.length > 1 && g.accounts.map((a) => <div key={a.code}>{row(a.name, a.amountMinor, 'sub')}</div>)}
          </div>
        ))}
        {row('Left after running the buildings', s.buildingSurplusMinor, 'total')}
      </section>
      <section>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500"><span className="text-bronze-600">2</span> · The company</p>
        {row('Lotus Living management fee', s.managementFee.amountMinor, 'out', s.managementFee.basis !== 'POSTED' && <span className="ml-2 align-middle"><Badge value={`${s.managementFee.basis === 'MIXED' ? 'Partly e' : 'E'}stimated · ${s.managementFee.pct}%`} tone="warning" /></span>)}
        {row('Left for the company', s.netToCompanyMinor, 'net')}
      </section>
    </div>
  );
}

/** The shares held today as one bar, with what is left to 100%. */
export function OwnershipBar({ o }: { o: Ownership }) {
  const colours = ['bg-bronze-500', 'bg-ink-700', 'bg-bronze-300', 'bg-emerald-600', 'bg-sky-600', 'bg-rose-500', 'bg-amber-500'];
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-ink-100" role="img" aria-label={`Shares held: ${o.totalPct}%`}>
        {o.current.map((c, i) => <div key={c.investorId} className={colours[i % colours.length]} style={{ width: `${c.sharePct}%` }} title={`${c.investorName} ${c.sharePct}%`} />)}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-600">
        {o.current.map((c, i) => <span key={c.investorId} className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${colours[i % colours.length]}`} />{c.investorName} {c.sharePct}%</span>)}
        <span className={o.totalPct === 100 ? 'text-emerald-700' : 'text-amber-700'}>{o.totalPct === 100 ? '100% allocated' : `${o.totalPct}% allocated · ${o.unallocatedPct}% with nobody`}</span>
      </div>
    </div>
  );
}

/** Add or change a holding. A new holding can name an investor not yet on file. */
export function HoldingModal({ entityId, currency, holding, onClose }: { entityId: string; currency: string; holding: Holding | null; onClose: () => void }) {
  const qc = useQueryClient();
  const investors = useQuery({ queryKey: ['/investors', 'all'], queryFn: () => ownersApi.investors(), enabled: !holding });
  const [form, setForm] = useState({
    investorId: holding?.investorId ?? '', newName: '', newEmail: '', newKind: 'INDIVIDUAL' as InvestorKind,
    sharePct: holding ? String(holding.sharePct) : '', fromDate: holding?.fromDate ?? todayIso(), toDate: holding?.toDate ?? '',
    capital: holding?.capitalMinor ? toMajor(holding.capitalMinor) : '', notes: holding?.notes ?? '',
  });
  const isNew = form.investorId === '__new';
  const save = useMutation({
    mutationFn: async () => {
      const body = { sharePct: Number(form.sharePct), fromDate: form.fromDate, toDate: form.toDate || null, capitalMinor: form.capital ? toMinor(form.capital) ?? null : null, notes: form.notes || null };
      if (holding) return ownersApi.updateHolding(entityId, holding.id, body);
      const investorId = isNew ? (await ownersApi.createInvestor({ name: form.newName, email: form.newEmail || null, kind: form.newKind })).id : form.investorId;
      return ownersApi.addHolding(entityId, { investorId, ...body });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['owners'] }); void qc.invalidateQueries({ queryKey: ['/investors'] }); onClose(); },
  });
  const remove = useMutation({
    mutationFn: () => ownersApi.removeHolding(entityId, holding!.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['owners'] }); onClose(); },
  });
  const canDelete = useCan('investors.delete');
  const pct = Number(form.sharePct);
  const invalid = !(pct > 0 && pct <= 100) || !form.fromDate || (!holding && !form.investorId) || (isNew && !form.newName.trim());
  return (
    <Modal open onClose={onClose} title={holding ? holding.investorName : 'Add an investor to the company'}
      footer={<>
        {holding && canDelete && <Button variant="danger" className="mr-auto" onClick={() => remove.mutate()} disabled={remove.isPending}>Remove</Button>}
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={invalid || save.isPending}>{save.isPending ? 'Saving…' : holding ? 'Save' : 'Add'}</Button>
      </>}>
      <div className="space-y-3">
        <p className="text-[13px] leading-relaxed text-ink-500">Profit is split by these shares. The shares of the company on any one day cannot add up to more than 100% — to change someone's share from a date, end this holding the day before and add a new one.</p>
        <FormGrid>
          {!holding && (
            <Wide><Field label="Investor">
              <Select value={form.investorId} onChange={(e) => setForm({ ...form, investorId: e.target.value })} className="w-full">
                <option value="">Choose…</option>
                {investors.data?.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                <option value="__new">+ Someone new…</option>
              </Select>
            </Field></Wide>
          )}
          {isNew && <>
            <Field label="Name"><Input value={form.newName} onChange={(e) => setForm({ ...form, newName: e.target.value })} className="w-full" /></Field>
            <Field label="Kind"><Select value={form.newKind} onChange={(e) => setForm({ ...form, newKind: e.target.value as InvestorKind })} className="w-full">{Object.entries(INVESTOR_KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></Field>
            <Wide><Field label="Email (statements go here)"><Input type="email" value={form.newEmail} onChange={(e) => setForm({ ...form, newEmail: e.target.value })} className="w-full" /></Field></Wide>
          </>}
          <Field label="Share of the company (%)"><Input inputMode="decimal" value={form.sharePct} onChange={(e) => setForm({ ...form, sharePct: e.target.value })} className="w-full" placeholder="25" /></Field>
          <Field label={`Capital put in (${currency})`}><Input inputMode="decimal" value={form.capital} onChange={(e) => setForm({ ...form, capital: e.target.value })} className="w-full" /></Field>
          <Field label="Investor since"><Input type="date" value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} className="w-full" /></Field>
          <Field label="Until (blank = still holds)"><Input type="date" value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} className="w-full" /></Field>
          <Wide><Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full" /></Field></Wide>
        </FormGrid>
        <FormError error={save.error ?? remove.error} />
      </div>
    </Modal>
  );
}

/** The rendered statement in a frame, with Issue / Send / Download for those who may. */
export function StatementPreview({ statement, onClose }: { statement: OwnerStatement; onClose: () => void }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [s, setS] = useState(statement);
  const [sentNote, setSentNote] = useState<string | null>(null);
  const canIssue = useCan('ownerStatements.issue');
  const canSend = useCan('ownerStatements.send');
  useEffect(() => {
    let href: string | null = null;
    setUrl(null);
    statementBlobUrl(s.id).then((h) => { href = h; setUrl(h); }).catch((e: Error) => setErr(e.message));
    return () => { if (href) URL.revokeObjectURL(href); };
  }, [s.id, s.status]);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['owners'] }); };
  const issue = useMutation({ mutationFn: () => ownersApi.issue(s.id), onSuccess: (x) => { setS(x); refresh(); } });
  const send = useMutation({
    mutationFn: () => ownersApi.send(s.id),
    onSuccess: (r) => { setS(r.statement); refresh(); setSentNote(`${r.sent} letter${r.sent === 1 ? '' : 's'} sent${r.skipped.length ? ` · not sent to ${r.skipped.map((x) => `${x.name} (${x.reason.toLowerCase()})`).join(', ')}` : ''}`); },
  });
  return (
    <Modal open onClose={onClose} size="lg" title={`${s.entityName}`}
      footer={<div className="flex w-full flex-wrap items-center justify-end gap-2">
        <span className="mr-auto text-[12px] text-ink-500">{periodLabel(s.periodFrom, s.periodTo)} · <Badge value={s.status} tone={s.status === 'ISSUED' ? 'good' : 'neutral'} />{s.sentAt && <span> · sent {formatDate(s.sentAt)}</span>}</span>
        <Button variant="secondary" size="sm" icon="Download" onClick={() => void downloadStatement(s)}>Download</Button>
        {s.status === 'DRAFT' && canIssue && <Button size="sm" variant="secondary" onClick={() => issue.mutate()} disabled={issue.isPending}><Stamp className="mr-1 h-3.5 w-3.5" />{issue.isPending ? 'Issuing…' : 'Issue'}</Button>}
        {s.status === 'ISSUED' && canSend && <Button size="sm" onClick={() => send.mutate()} disabled={send.isPending}><Send className="mr-1 h-3.5 w-3.5" />{send.isPending ? 'Sending…' : s.sentAt ? 'Send again' : 'Send to investors'}</Button>}
      </div>}>
      <div className="-m-6">
        {sentNote && <p className="border-b border-emerald-100 bg-emerald-50 px-6 py-2 text-[12.5px] text-emerald-800">{sentNote}</p>}
        {s.status === 'DRAFT' && canIssue && <p className="border-b border-amber-100 bg-amber-50 px-6 py-2 text-[12.5px] text-amber-800">A draft can be rebuilt. Issuing locks these figures as the ones the investors are told.</p>}
        <div className="px-6 pt-2"><FormError error={issue.error ?? send.error} />{err && <p className="text-[13px] text-red-700">{err}</p>}</div>
        {url ? <iframe title="Statement" src={url} sandbox="" className="h-[62vh] w-full border-0 bg-white" /> : !err && <div className="p-6"><SkeletonRows rows={8} cols={3} /></div>}
      </div>
    </Modal>
  );
}

/** Statements of one company (or several), newest first; Build for a period when the user may. */
export function StatementsPanel({ entityId, entityIds, showCompany = false }: { entityId?: string; entityIds?: string[]; showCompany?: boolean }) {
  const qc = useQueryClient();
  const canBuild = useCan('ownerStatements.create');
  const list = useQuery({ queryKey: ['owners', 'statements', entityId ?? entityIds?.join(',') ?? 'all'], queryFn: () => ownersApi.statements(entityId ? { entityId } : {}) });
  const rows = (list.data ?? []).filter((s) => !entityIds || entityIds.includes(s.entityId));
  const [open, setOpen] = useState<OwnerStatement | null>(null);
  const last = quarterOf(todayIso(), -1);
  const [period, setPeriod] = useState({ from: last.from, to: last.to });
  const build = useMutation({
    mutationFn: () => ownersApi.build({ entityId: entityId!, ...period }),
    onSuccess: (s) => { void qc.invalidateQueries({ queryKey: ['owners'] }); setOpen(s); },
  });
  return (
    <div className="space-y-2.5">
      {entityId && canBuild && (
        <div className="surface flex flex-wrap items-end gap-3 p-4">
          <Field label="From"><Input type="date" value={period.from} onChange={(e) => setPeriod({ ...period, from: e.target.value })} /></Field>
          <Field label="To"><Input type="date" value={period.to} max={todayIso()} onChange={(e) => setPeriod({ ...period, to: e.target.value })} /></Field>
          <Button icon="FilePlus2" onClick={() => build.mutate()} disabled={build.isPending || !period.from || !period.to}>{build.isPending ? 'Building…' : 'Build statement'}</Button>
          <p className="basis-full text-[12px] text-ink-400">Builds a draft from the ledger as it is now. Rebuilding a draft refreshes it; an issued statement is locked.</p>
          <div className="basis-full"><FormError error={build.error} /></div>
        </div>
      )}
      <div className="surface divide-y divide-ink-100">
        {list.isLoading && <div className="p-4"><SkeletonRows rows={4} cols={4} /></div>}
        {!list.isLoading && !rows.length && <p className="p-4 text-[12.5px] text-ink-500">No statements yet.</p>}
        {rows.map((s) => (
          <button key={s.id} onClick={() => setOpen(s)} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-left transition hover:bg-ink-50/60">
            <FileText className="h-4 w-4 shrink-0 text-ink-400" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium text-ink-900">{showCompany && <span>{s.entityName} · </span>}{periodLabel(s.periodFrom, s.periodTo)}</div>
              <div className="text-[12px] text-ink-500">{s.status === 'ISSUED' ? `Issued ${s.issuedAt ? formatDate(s.issuedAt) : ''}${s.issuedByName ? ` by ${s.issuedByName}` : ''}` : 'Draft'}{s.sentAt ? ` · sent ${formatDate(s.sentAt)} (${s.sentOutboxIds.length} letter${s.sentOutboxIds.length === 1 ? '' : 's'})` : ''}{s.managementFeeBasis === 'ESTIMATED' ? ' · fee estimated' : ''}</div>
            </div>
            <div className="text-right"><Money minor={s.netToCompanyMinor} currency={s.currency} className="text-[13px] font-medium text-ink-900" /><div className="text-[11px] text-ink-400">left for the company</div></div>
            <Badge value={s.status} tone={s.status === 'ISSUED' ? 'good' : 'neutral'} />
            <span className="hidden text-ink-400 sm:inline" onClick={(e) => { e.stopPropagation(); void downloadStatement(s); }} title="Download"><Download className="h-4 w-4" /></span>
          </button>
        ))}
      </div>
      {open && <StatementPreview statement={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
